import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { HomePlaylistCard as CardModel, HomePlaylistSort } from './buildHomePlaylistCards';
import { playlistPath } from '../playlists/playlistRoutes';
import { sortHomePlaylistCards } from './buildHomePlaylistCards';
import { HomePlaylistCard } from './HomePlaylistCard';
import { HomePlaylistPreview } from './HomePlaylistPreview';

type HomePlaylistsSectionProps = {
  cards: CardModel[];
  playingCount: number;
  canManage: boolean;
};

export function HomePlaylistsSection({ cards, playingCount, canManage }: HomePlaylistsSectionProps) {
  const { t } = useTranslation('home');
  const [sort, setSort] = useState<HomePlaylistSort>('edited');
  const [view, setView] = useState<'grid' | 'list'>('grid');

  const sorted = useMemo(() => sortHomePlaylistCards(cards, sort), [cards, sort]);

  return (
    <section className="flex min-w-0 flex-[999_1_560px] flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h2
            className="m-0 text-[20px] font-extrabold tracking-[-0.02em] sm:text-[22px]"
            style={{ color: 'var(--gs-ink)' }}
          >
            {t('playlists.title')}
          </h2>
          <span className="text-sm" style={{ color: 'var(--gs-muted)' }}>
            {t('playlists.summary', { playing: playingCount, count: cards.length })}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
          <span className="text-[13px]" style={{ color: 'var(--gs-muted)' }}>
            {t('playlists.sortBy')}
          </span>
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value as HomePlaylistSort)}
            className="cursor-pointer rounded-[9px] border px-2.5 py-2 text-[13px] font-semibold"
            style={{
              color: 'var(--gs-ink)',
              background: 'var(--gs-panel)',
              borderColor: 'var(--gs-line)',
              fontFamily: 'inherit',
            }}
          >
            <option value="edited">{t('playlists.sort.edited')}</option>
            <option value="name">{t('playlists.sort.name')}</option>
            <option value="screens">{t('playlists.sort.screens')}</option>
          </select>
          <div
            className="flex overflow-hidden rounded-[9px] border"
            style={{ borderColor: 'var(--gs-line)', background: 'var(--gs-panel)' }}
          >
            <button
              type="button"
              title={t('playlists.gridView')}
              onClick={() => setView('grid')}
              className="grid h-[34px] w-9 place-items-center border-0"
              style={{
                background: view === 'grid' ? 'var(--gs-panel2)' : 'transparent',
                color: view === 'grid' ? 'var(--gs-ink)' : 'var(--gs-muted)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <rect x="4" y="4" width="7" height="7" rx="1.5" />
                <rect x="13" y="4" width="7" height="7" rx="1.5" />
                <rect x="4" y="13" width="7" height="7" rx="1.5" />
                <rect x="13" y="13" width="7" height="7" rx="1.5" />
              </svg>
            </button>
            <button
              type="button"
              title={t('playlists.listView')}
              onClick={() => setView('list')}
              className="grid h-[34px] w-9 place-items-center border-0 border-s"
              style={{
                borderColor: 'var(--gs-line)',
                background: view === 'list' ? 'var(--gs-panel2)' : 'transparent',
                color: view === 'list' ? 'var(--gs-ink)' : 'var(--gs-muted)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <p className="m-0 text-sm" style={{ color: 'var(--gs-muted)' }}>
          {canManage ? t('playlists.emptyManager') : t('playlists.emptyViewer')}
        </p>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(min(100%,270px),1fr))]">
          {sorted.map((card) => (
            <HomePlaylistCard key={card.id} card={card} canManage={canManage} />
          ))}
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-2xl border"
          style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
        >
          {sorted.map((card, index) => (
            <Link
              key={card.id}
              to={playlistPath(card.id)}
              className="flex items-center gap-3.5 px-4 py-3 no-underline transition-colors hover:bg-[var(--gs-panel2)]"
              style={{ borderTop: index ? '1px solid var(--gs-line)' : 'none' }}
            >
              <HomePlaylistPreview
                previews={card.previews.slice(0, 1)}
                compact
                className="h-12 w-[76px] shrink-0 rounded-lg"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[15px] font-bold" style={{ color: 'var(--gs-ink)' }}>
                  {card.name}
                </span>
                <span className="text-[13px]" style={{ color: 'var(--gs-muted)' }}>
                  {[card.itemsLabel, card.durationLabel, card.screensLabel]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span
                className="text-[13px] font-bold"
                style={{
                  color: card.playing ? 'var(--gs-ok)' : 'var(--gs-muted)',
                }}
              >
                {card.statusLabel}
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
